import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const Netflix = () => {
  return (
    <div className="min-h-screen pt-32 pb-20 px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/projects">
          <motion.button
            whileHover={{ x: -5 }}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            Back to Projects
          </motion.button>
        </Link>

        {/* Project Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Netflix <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">Clone</span>
          </h1>
          <p className="text-xl text-gray-400">
            A fully functional streaming platform microfrontend
          </p>
        </motion.div>

        {/* Project Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="relative rounded-2xl overflow-hidden mb-12 border border-white/10"
        >
          <img
            src="https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?w=1200&h=600&fit=crop"
            alt="Netflix Clone"
            className="w-full h-auto"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgb(10,10,15)] via-transparent to-transparent" />
        </motion.div>

        {/* Project Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[rgb(15,20,30)] rounded-2xl p-8 border border-white/5 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-6">Project Overview</h2>
          <div className="space-y-4 text-gray-400 leading-relaxed">
            <p>
              This Netflix clone is a fully functional streaming platform built as a microfrontend application. 
              It demonstrates modern web development practices including module federation, lazy loading, and 
              responsive design.
            </p>
            <p>
              The application features user authentication, video streaming capabilities, personalized recommendations, 
              and a beautiful, responsive UI that mirrors the Netflix experience.
            </p>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-semibold text-white mb-4">Key Features</h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {[
                'User Authentication & Authorization',
                'Video Streaming & Playback',
                'Personalized Recommendations',
                'Search & Filter Functionality',
                'Responsive Design',
                'Watch History Tracking'
              ].map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-400">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-semibold text-white mb-4">Technologies Used</h3>
            <div className="flex flex-wrap gap-2">
              {['React', 'TypeScript', 'Webpack 5', 'Module Federation', 'Node.js', 'MongoDB', 'JWT'].map((tech) => (
                <span key={tech} className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium border border-red-500/20">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Coming Soon Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 text-center"
        >
          <h3 className="text-xl font-semibold text-blue-400 mb-2">
            🚀 Coming Soon!
          </h3>
          <p className="text-gray-400">
            This microfrontend is currently under development. Check back soon for the live demo!
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Netflix;